import type { Task } from '@medplum/fhirtypes';
import { getTaskTarget, type TaskTarget } from './taskUtils';
import { durationToMinutes, formatSleepDuration, isHourUnit } from './vitals';

const COMPARATOR_SYMBOLS: Record<NonNullable<TaskTarget['comparator']>, string> = {
  '>=': '≥',
  '<=': '≤',
  '>': '>',
  '<': '<',
};

/** Format a lifestyle-measurement target as "≥ 10,000 steps/day" (no comparator symbol for a
 * "meet exactly" target). The value uses locale-aware grouping so `es` gets "10.000", not "10,000".
 *
 * `unitOverride` supplies a localized unit (the persisted `target.unit` is English). It is required
 * so every call site decides how to localize — pass the localized unit, or `undefined` to use the
 * persisted English unit (e.g. for a clinical measurement, or a surface not yet localized).
 *
 * Hours-valued targets (currently only sleep) render as hours-and-minutes ("≥ 7h") to match how the
 * recorded value is shown, rather than the catalog's raw "7 hours/night"; the h/m unit is
 * language-neutral, so `unitOverride` doesn't apply. */
export function formatTarget(target: TaskTarget, localeString: string, unitOverride: string | undefined): string {
  const comparator = target.comparator ? `${COMPARATOR_SYMBOLS[target.comparator]} ` : '';
  if (isHourUnit(target.unit)) {
    return `${comparator}${formatSleepDuration(durationToMinutes(target.value, target.unit, undefined))}`;
  }
  const unitValue = unitOverride ?? target.unit;
  const unit = unitValue ? ` ${unitValue}` : '';
  return `${comparator}${new Intl.NumberFormat(localeString).format(target.value)}${unit}`;
}

/** The "Target: …" line for a lifestyle measurement while the item is open; null once completed
 * (the recorded value is shown instead) or for a task with no target. `unitOverride` is a localized
 * unit forwarded to {@link formatTarget} (required — pass `undefined` to use the persisted unit). */
export function targetLineFor(
  task: Task,
  isCompleted: boolean,
  label: string,
  localeString: string,
  unitOverride: string | undefined,
): string | null {
  if (isCompleted) return null;
  const target = getTaskTarget(task);
  return target ? `${label}: ${formatTarget(target, localeString, unitOverride)}` : null;
}
